/* eslint-disable @blitz/lines-around-comment */
import React, { useEffect, useState } from 'react';

// Figma design image assets
const imgScreenshot = '/images/fallback.avif';
const videoBackground = '/videos/bgVideo.mp4';

// Project Cards Data Structure
interface ProjectCard {
  imgUrl: string;
  link?: string;
}

const ProjectCards: ProjectCard[] = [
  {
    imgUrl: '/images/hero-orb.avif',
    // link: 'https://seo-hero-section-q6hd-dualite.netlify.app/',
  },
  {
    imgUrl: '/images/hero-claymist.avif',
    // link: 'https://ymnasium-website-bzsb-dualite.netlify.app',
  },
  {
    imgUrl: '/images/hero-converty.avif',
    // link: 'https://seo-hero-section-q6hd-dualite.netlify.app/',
  },
  {
    imgUrl: 'images/hero-peekseo.avif',
    // link: 'https://orm-landing-page-58wu-dualite.netlify.app',
  },
  {
    imgUrl: '/images/hero-poolsuite.avif',
    // link: 'https://seo-dualite-9mxr-dualite.netlify.app/',
  },
  {
    imgUrl: '/images/hero-aerotyne.avif',
    // link: 'https://www.talentmind.tech/',
  },
];

// Testimonials Data Structure
interface Testimonial {
  quote: string;
  author: string;
  company: string;
}

const Testimonials: Testimonial[] = [
  {
    quote: 'Dualite is absolutely magical.',
    author: 'LS Venkatesh',
    company: 'India',
  },
  {
    quote: 'The fastest way to prototype my ideas.',
    author: 'Michael Roberts',
    company: 'United States',
  },
  {
    quote: 'I made my first app in minutes with Dualite!',
    author: 'Augusto Silva',
    company: 'Brazil',
  },
];

export default function HeroCarousel() {
  // Start at the middle set to allow seamless looping in both directions
  const [currentIndex, setCurrentIndex] = useState(ProjectCards.length);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isAutoplaying, setIsAutoplaying] = useState(true);

  // Testimonial rotation state
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [isTestimonialFading, setIsTestimonialFading] = useState(false);

  // Create infinite loop array by duplicating cards
  const infiniteCards = [...ProjectCards, ...ProjectCards, ...ProjectCards];

  // Carousel periodic switching with slide animation
  useEffect(() => {
    if (!isAutoplaying) {
      return () => {
        clearInterval(interval);
      };
    }

    const interval = setInterval(() => {
      setIsTransitioning(true);
      setCurrentIndex((prev) => prev + 1);
    }, 3000); // Switch every 3 seconds

    return () => {
      clearInterval(interval);
    };
  }, [isAutoplaying]);

  // Reset position when reaching the end of second set to maintain infinite loop
  useEffect(() => {
    if (currentIndex >= 2 * ProjectCards.length) {
      // Wait for transition to complete, then instantly reset to middle set
      setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(ProjectCards.length);

        /*
         * Re-enable transition after reset
         * We need to let the render happen with transition: none
         * Then next tick re-enable
         */
        setTimeout(() => {
          setIsTransitioning(true);
        }, 50);
      }, 600); // Match transition duration
    }
  }, [currentIndex]);

  // Testimonial rotation - switch every 5 seconds with fade animation
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTestimonialFading(true);

      // After fade out animation, switch testimonial
      setTimeout(() => {
        setCurrentTestimonialIndex((prev) => (prev + 1) % Testimonials.length);
        setIsTestimonialFading(false);
      }, 600); // Match CSS fade-out animation duration
    }, 5000); // Switch every 5 seconds

    return () => clearInterval(interval);
  }, []);

  // Carousel navigation functions
  const handlePrevious = () => {
    setIsAutoplaying(false);
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev - 1);
  };

  const handleNext = () => {
    setIsAutoplaying(false);
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev + 1);
  };

  const handleDotClick = (index: number) => {
    setIsAutoplaying(false);
    setIsTransitioning(true);
    setCurrentIndex(ProjectCards.length + index);
  };

  return (
    <div className="@container relative w-full h-full flex-shrink-0 overflow-hidden bg-[#0F0F10] rounded-[2cqh] ">
      {/* Main Video Background with Image Fallback */}
      <div className="absolute inset-0 z-10">
        <video
          src={videoBackground}
          poster={imgScreenshot}
          className="w-full object-cover relative"
          style={{ height: 'calc(100% + 7cqh)' }}
          autoPlay
          loop
          muted
          playsInline
          onError={(e) => {
            // Hide video and show fallback image if video fails to load
            const video = e.currentTarget;
            video.style.display = 'none';

            const fallback = video.nextElementSibling as HTMLImageElement;

            if (fallback) {
              fallback.style.display = 'block';
            }
          }}
        />
        {/* Fallback image in case video doesn't load */}
        <img
          src={imgScreenshot}
          alt="App Preview"
          className="w-full h-full object-cover absolute inset-0 bottom-[-10cqh] "
          style={{ display: 'none' }}
        />
        {/* Black overlay with 25% opacity */}
        {/* <div className="absolute inset-0 bg-black opacity-30" /> */}
      </div>

      {/* Testimonials Text */}
      <div className="w-full text-center absolute top-[5cqh] z-20 px-4">
        <div
          className={isTestimonialFading ? 'testimonial-fade-out' : 'testimonial-fade-in'}
          key={currentTestimonialIndex}
        >
          <p
            className="text-white/70 font-normal leading-relaxed mb-[1cqh] @md:text-[4cqi]"
            style={{ fontFamily: 'Inter Display' }}
          >
            "{Testimonials[currentTestimonialIndex].quote}"
          </p>
          <p className="text-white/70 font-normal text-[2.5cqi]" style={{ fontFamily: 'Inter Display' }}>
            ~ <span className="font-medium">{Testimonials[currentTestimonialIndex].author}</span>,{' '}
            {Testimonials[currentTestimonialIndex].company}
          </p>
        </div>
      </div>

      {/* Carousel Images */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 z-20 overflow-hidden w-full">
        <div
          className={`
            flex gap-[2cqi] items-center will-change-transform
            /* --- 1. DEFINE VARIABLES BASED ON CONTAINER WIDTH --- */
            /* Default (Small < 42rem): Big cards for mobile */
            [--active-width:80cqi]
            [--inactive-width:15cqi]

            /* Medium (@2xl >= 42rem): Intermediate size */
            @2xl:[--active-width:60cqi]
            @2xl:[--inactive-width:25cqi]

            /* Large (@6xl >= 72rem): Original compact size */
            @6xl:[--active-width:45cqi]
            @6xl:[--inactive-width:20cqi]
          `}
          style={
            {
              // @ts-ignore: Custom CSS variables for animation logic
              '--current-index': currentIndex,

              /* --- 2. DYNAMIC CALCULATION --- */
              /*
               * Layout Logic:
               * Step   = Inactive Width + Gap (2cqi)
               * Offset = Center(50cqi) - Half Active Width
               */
              transform: `translateX(calc(
                (50cqi - (var(--active-width) / 2)) - 
                (var(--current-index) * (var(--inactive-width) + 2cqi))
              ))`,
              transition: isTransitioning ? 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
            } as React.CSSProperties
          }
        >
          {infiniteCards.map((card, index) => {
            const isCenter = index === currentIndex;
            const isNearCenter = Math.abs(index - currentIndex) <= 2;

            return (
              <div
                key={index}
                className={`
                  overflow-hidden flex-shrink-0 cursor-pointer bg-black
                  transition-all duration-500 cubic-bezier(0.4, 0, 0.2, 1)
                  flex items-start justify-center aspect-[1.6]
                  
                  /* --- 3. APPLY DYNAMIC WIDTHS --- */
                  /* Inactive State */
                  w-[var(--inactive-width)] 
                  opacity-70 border-[2px] border-white/10 rounded-[6px]
                  
                  /* Active State */
                  data-[active=true]:w-[var(--active-width)] 
                  
                  data-[active=true]:opacity-100 
                  data-[active=true]:border-[6px] data-[active=true]:border-white/50 
                  data-[active=true]:rounded-[9px] data-[active=true]:bg-[#343434]/70
                `}
                data-active={isCenter}
                onClick={() => {
                  if (card.link) {
                    window.open(card.link, '_blank', 'noopener,noreferrer');
                  }
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && card.link) {
                    e.preventDefault();
                    window.open(card.link, '_blank', 'noopener,noreferrer');
                  }
                }}
              >
                <img
                  src={card.imgUrl}
                  alt=""
                  className="w-full h-full object-cover rounded-[inherit]"
                  loading={isNearCenter ? 'eager' : 'lazy'}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Carousel Controls */}
      <div className="absolute bottom-[10cqh] @2xl:bottom-[18cqh] left-0 right-0 flex items-center justify-center gap-[4cqi] px-4 z-30">
        {/* Previous Button */}
        <button
          type="button"
          onClick={handlePrevious}
          className="w-[5cqh] h-[5cqh] flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors backdrop-blur-sm border border-white/20"
          aria-label="Previous slide"
        >
          <svg
            width="50%"
            height="50%"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="text-white"
          >
            <path
              d="M10 12L6 8L10 4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Indicator Dots */}
        <div className="flex items-center gap-[1cqi]">
          {ProjectCards.map((_, index) => {
            const actualIndex = currentIndex % ProjectCards.length;
            const isActive = index === actualIndex;
            return (
              <button
                key={index}
                type="button"
                onClick={() => handleDotClick(index)}
                className={`transition-all duration-300 rounded-full ${
                  isActive ? 'w-[1.5cqh] h-[1.5cqh] bg-white' : 'w-[1.5cqh] h-[1.5cqh] bg-white/40 hover:bg-white/60'
                }`}
                aria-label={`Go to slide ${index + 1}`}
                aria-current={isActive ? 'true' : 'false'}
              />
            );
          })}
        </div>

        {/* Next Button */}
        <button
          type="button"
          onClick={handleNext}
          className="w-[5cqh] h-[5cqh] flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors backdrop-blur-sm border border-white/20"
          aria-label="Next slide"
        >
          <svg
            width="50%"
            height="50%"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="text-white"
          >
            <path
              d="M6 12L10 8L6 4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Stats - Bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-[8cqh] @xl:h-[15cqh] flex items-center justify-around px-[4cqi] z-30 border-t-[3.5px] border-white/60">
        {/* 1. Glass Background Layer */}
        <div className="absolute inset-0 backdrop-blur-md" />

        {/* 2. Top Border Layer (Simulating ::before) */}
        {/* <div
          className="absolute left-0 right-0 top-0 h-[3.5px] z-40"
          style={{
            background: `
radial-gradient(80.79% 50% at 50% 50%, #8C4424 0%, rgba(140, 68, 36, 0) 100%),
radial-gradient(2.03% 50% at 50% 50%, #F53E02 0%, rgba(245, 62, 2, 0.58) 100%),
linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%);
            `,
          }}
        /> */}

        {/* 100k+ users */}
        <div className="text-center relative z-10">
          <p className="text-white text-[4cqi] font-medium leading-[1.2] mb-[0.5cqh]">100K+</p>
          <p className="text-white text-[1.5cqi] font-normal opacity-80">Users</p>
        </div>
        {/* 255k+ Apps */}
        <div className="text-center relative z-10">
          <p className="text-white text-[4cqi] font-medium leading-[1.2] mb-[0.5cqh]">255K+</p>
          <p className="text-white text-[1.5cqi] font-normal opacity-80">Apps</p>
        </div>
        {/* 100+ Countries */}
        <div className="text-center relative z-10">
          <p className="text-white text-[4cqi] font-medium leading-[1.2] mb-[0.5cqh]">100+</p>
          <p className="text-white text-[2.5cqi] font-normal opacity-80">Countries</p>
        </div>
      </div>
    </div>
  );
}
